import React, { useEffect, useMemo, useState } from 'react';
import {
  ArrowUpRight,
  ChevronLeft,
  ChevronRight,
  FileText,
  Github,
  Linkedin,
  Lock,
  Mail,
  X,
} from 'lucide-react';
import './App.css';

const assetAliases = {
  'IMG_3052.jpg': 'IMG_3052 copy.jpg',
  'IMG_4360.jpg': 'IMG_4360.JPG',
};

const resolvePublicAsset = (assetPath) => {
  if (!assetPath) return assetPath;
  if (/^(https?:)?\/\//.test(assetPath) || assetPath.startsWith('data:')) return assetPath;

  const normalizedPath = assetPath.startsWith('/') ? assetPath.slice(1) : assetPath;
  return `/${assetAliases[normalizedPath] || normalizedPath}`;
};

export default function Portfolio() {
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedBlog, setSelectedBlog] = useState(null);
  const [selectedBlogImage, setSelectedBlogImage] = useState(null);
  const [blogPasswordInput, setBlogPasswordInput] = useState('');
  const [blogUnlockError, setBlogUnlockError] = useState('');
  const [decryptedBlogContent, setDecryptedBlogContent] = useState('');
  const [isUnlockingBlog, setIsUnlockingBlog] = useState(false);
  const [selectedAlbum, setSelectedAlbum] = useState(null);
  const [selectedPhoto, setSelectedPhoto] = useState(null);

  const projects = [
    {
      title: 'Parameterized ALU',
      description: 'Configurable arithmetic logic unit designed in synthesizable Verilog.',
      detailDescription:
        'Designed a parameterized arithmetic logic unit in Verilog with configurable data width and reusable operations for FPGA and digital logic projects.',
      tags: ['Verilog', 'FPGA', 'Digital Logic'],
      image: '/ALU.jpg',
      year: '2026',
      repoUrl: 'https://github.com/shmoos/Parameterized-ALU-',
    },
    {
      title: 'Self-Hosted Cloud Server',
      description: 'Personal cloud stack on Raspberry Pi 5 with public access, reverse proxy routing, and monitoring.',
      detailDescription:
        'Deployed a personal cloud platform on a Pi 5 using Docker Compose. Configured Caddy, Cloudflare Tunnel, Uptime Kuma, and Grafana to expose services securely and track uptime.',
      tags: ['Docker', 'Cloudflare', 'Caddy', 'Grafana'],
      image: '/personal-server.jpg',
      year: '2026',
    },
    {
      title: 'Mental Health Therapy App',
      description: 'AI-driven therapeutic exercises, check-ins, journaling prompts, and mood tracking for iOS.',
      detailDescription:
        'Built an iOS app focused on mental wellness with AI-powered journaling prompts, therapeutic exercises, recurring check-ins, and secure cloud data sync for private progress tracking.',
      tags: ['Swift', 'Firebase', 'ML'],
      image: '/IMG_1609.jpg',
      year: '2025',
      repoUrl: 'https://github.com/shmoos/Mindful_Team10',
    },
    {
      title: 'Real Time Hand Gesture Recognition',
      description: 'Computer vision pipeline for hand keypoint detection and gesture-based interaction.',
      detailDescription:
        'Created a real-time computer vision pipeline that detects hand keypoints and maps gestures to actions using OpenCV and MediaPipe.',
      tags: ['OpenCV', 'MediaPipe', 'Python'],
      image: '/IMG_1555.jpg',
      year: '2025',
      repoUrl: 'https://github.com/shmoos/HandKeyPointDeteciton',
    },
    {
      title: 'Custom MicroCenter LLM Chatbot',
      description: 'Retrieval-powered shopping assistant for product discovery and compatible PC parts.',
      detailDescription:
        'Built a retrieval-powered chatbot tuned for MicroCenter inventory. Designed prompt templates, product indexing, and ranking logic for budget and compatibility searches.',
      tags: ['Transformers', 'Python', 'HuggingFace'],
      image: '/IMG_1810.jpg',
      year: '2025',
      repoUrl: 'https://github.com/shmoos/MicroCenterLLM',
    },
    {
      title: 'CA School Board Crawler',
      description: 'Crawler that collects and normalizes California school board member data.',
      detailDescription:
        'Developed a crawler that standardizes California school board member data and handles inconsistent source formatting across districts.',
      tags: ['Node', 'C++', 'JavaScript'],
      image: '/IMG_1443.jpg',
      year: '2025',
      repoUrl: 'https://github.com/shmoos/School-Board-Search-CA',
    },
  ];

  const careerEntries = [
    { 
      company: 'Synopsys', 
      role: 'FPGA Prototyping Intern',
      location: 'Santa Clara, CA', 
      dates: 'May 2026 - August 2026',
      highlights: [
        'Assembled and configured high-density FPGA servers deployed by NVIDIA and other industry partners, ensuring hardware met strict performance and reliability standards',
        'Built, wired, and stress-tested server racks for Synopsys ZeBu emulation systems, supporting large-scale chip verification workflows',
      ],
      technologies: ['Vivado', 'Verilog','SystemVerilog']
    },
    {
      company: 'Aako',
      role: 'AR Spatial Analyst',
      location: 'Los Angeles, CA',
      dates: 'April 2025 - September 2025',
      highlights: [
        'Tested AR video workflows and debugged spatial behavior with ARCore.',
        'Supported UI layout review and beta testing for production reliability.',
      ],
      technologies: ['ARCore', 'ARKit', 'RealityKit', 'WebSocket'],
    },
    
  ];

  const photos = [
    { title: 'Japan', count: 6, image: '/IMG_1782.jpg' },
    { title: 'California', count: 4, image: '/IMG_2769.jpg' },
    { title: 'New York', count: 5, image: '/IMG_4117.jpg' },
  ];

  const detailImages = useMemo(
    () => ({
      'Japan': [
        '/IMG_1782.jpg',
        '/IMG_1555.jpg',
        '/IMG_1936.jpg',
        '/IMG_1810.jpg',
        '/IMG_1609.jpg',
        '/IMG_1443.jpg',
      ],
      'California': ['/IMG_2769.jpg', '/IMG_4360.jpg', '/IMG_7744.jpg', '/IMG_2700.jpg'],
      'New York': ['/IMG_3052.jpg', '/IMG_3613.jpg', '/IMG_3661.jpg', '/IMG_4055.jpg', '/IMG_4117.jpg'],
    }),
    []
  );

  const blogPosts = [
    {
      title: 'Raspberry Pie',
      date: 'Oct 27, 2025',
      excerpt: 'Notes on the RP2040 and small hardware projects.',
      readTime: '4 min read',
      sidebarImages: ['brendin-hoffman-xXrnMYeBcgI-unsplash.webp'],
      content: `
# The RP2040

Got this pi in the beginning of the summer to build projects and add to my portfolio and I didn't realize the potential that the 2040 chip contains. It's not bad but the processing power is something that doesnt fit my needs and will need to upgrade sooner or later.

## Projects so far

I've made some projects but so far and will upload them soon to this site. Still learning html and css LOL.
- Weather Station
- Potentiometer-driven light
- Interminal phototransistor system

## Future Plans

I really want to make this passion into something real. I want to try making a full fledged handheld system just by using a Pi 5 or other microcontrollers that meet the cpu processing benchmark. I've been interested in the ESP 32 series because of its small form factor and relatvie clockspeed that is higher than most Pi's.
`,
    },
    {
      title: 'Games in Progress.',
      date: 'Oct 27, 2025',
      excerpt: 'Games that I am currently playing and enjoying.',
      readTime: '8 min read',
      sidebarImages: ['Celeste.jpg', 'hades.jpg', 'OP2.jpg'],
      content: `
# Games being played currently

It's been quite some time since I picked up a game and played it for hours on end and thouroughly enjoying the experiance. Although recently I've been playing some RPG based games and It's honestly not bad at all and rather gripping.

## Games List

- Octopath II
- Celeste
- Hades

## Covering these games

I did some research on what games to play that I don't have to commit an arm and leg to and one of the games that I found out about was Octopath II.

## Finishing Touches

Overall I enjoy all these games are pretty enjoyable and are great pastimes.
`,
    },
    {
      title: 'Japan Trip 204',
      date: 'June 18th, 2024',
      excerpt: 'My take on Japan.',
      readTime: '8 min read',
      sidebarImages: ['/IMG_1782.jpg', '/IMG_1612.jpg', '/IMG_1443.jpg'],
      content: `
# Tokyo

Arrived at night. It was drizzling by the time we had go to our hotels surrounding area.

## Citylife

The dynamic of living in a big city like Tokyo can be overwhelming.

## Food

The food in Japan was amazing my family and I had little to no trouble deciding where to eat because of the abudnant halal options and I plan on going back soon.
`,
    },
    {
      title: 'Big Yikes',
      slug: 'big-yikes',
      date: 'March 3rd, 2025',
      excerpt: 'A private journal entry.',
      readTime: '5 min read',
      sidebarImages: [
        'B36C4EB3-1E88-4986-89D8-209E72111691_1_105_c.jpeg',
        '87D961B5-F2AA-4A37-A29E-8A4930ABBB92_4_5005_c.jpeg',
        'BE48D050-D606-47E6-9211-5C0912B7DF71_1_105_c.jpeg',
      ],
      protected: true,
    },
  ];

  const selectedBlogContent = selectedBlog?.protected ? decryptedBlogContent : selectedBlog?.content || '';
  const currentAlbumPhotos = useMemo(
    () => (selectedAlbum ? detailImages[selectedAlbum.title] || [] : []),
    [detailImages, selectedAlbum]
  );
  const selectedPhotoIndex = currentAlbumPhotos.findIndex((photo) => photo === selectedPhoto);

  useEffect(() => {
    setBlogPasswordInput('');
    setBlogUnlockError('');
    setDecryptedBlogContent('');
    setIsUnlockingBlog(false);
  }, [selectedBlog]);

  useEffect(() => {
    if (!selectedBlog?.protected || !selectedBlog?.slug) return;

    let cancelled = false;

    const loadProtectedContent = async () => {
      try {
        const response = await fetch(`/api/blog-content?slug=${encodeURIComponent(selectedBlog.slug)}`, {
          credentials: 'include',
        });
        if (!response.ok) return;
        const data = await response.json();
        if (!cancelled) setDecryptedBlogContent(data.content || '');
      } catch {
        // Silent cookie check only.
      }
    };

    loadProtectedContent();

    return () => {
      cancelled = true;
    };
  }, [selectedBlog]);

  useEffect(() => {
    const handleKeydown = (event) => {
      if (event.key === 'Escape') {
        setSelectedProject(null);
        setSelectedBlog(null);
        setSelectedBlogImage(null);
        setSelectedPhoto(null);
      }

      if (selectedPhotoIndex < 0 || currentAlbumPhotos.length < 2) return;
      if (event.key === 'ArrowLeft') showPreviousPhoto();
      if (event.key === 'ArrowRight') showNextPhoto();
    };

    if (selectedProject || selectedBlog || selectedBlogImage || selectedPhoto) {
      document.body.style.overflow = 'hidden';
      window.addEventListener('keydown', handleKeydown);
    }

    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleKeydown);
    };
  });

  const handleUnlockBlog = async (event) => {
    event.preventDefault();
    if (!selectedBlog?.protected || !selectedBlog?.slug) return;

    setIsUnlockingBlog(true);
    setBlogUnlockError('');

    try {
      const authResponse = await fetch('/api/blog-auth', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ slug: selectedBlog.slug, password: blogPasswordInput }),
      });

      if (!authResponse.ok) throw new Error('auth_failed');

      const contentResponse = await fetch(`/api/blog-content?slug=${encodeURIComponent(selectedBlog.slug)}`, {
        credentials: 'include',
      });

      if (!contentResponse.ok) throw new Error('content_failed');

      const data = await contentResponse.json();
      setDecryptedBlogContent(data.content || '');
    } catch (error) {
      setDecryptedBlogContent('');
      setBlogUnlockError(error.message === 'auth_failed' ? 'Incorrect password. Try again.' : 'Unable to load this post right now.');
    } finally {
      setIsUnlockingBlog(false);
    }
  };

  const showPreviousPhoto = () => {
    if (selectedPhotoIndex < 0 || currentAlbumPhotos.length < 2) return;
    const nextIndex = (selectedPhotoIndex - 1 + currentAlbumPhotos.length) % currentAlbumPhotos.length;
    setSelectedPhoto(currentAlbumPhotos[nextIndex]);
  };

  const showNextPhoto = () => {
    if (selectedPhotoIndex < 0 || currentAlbumPhotos.length < 2) return;
    const nextIndex = (selectedPhotoIndex + 1) % currentAlbumPhotos.length;
    setSelectedPhoto(currentAlbumPhotos[nextIndex]);
  };

  const renderPostContent = (content) =>
    content.split('\n').map((line, index) => {
      const trimmed = line.trim();
      if (!trimmed) return null;
      if (trimmed.startsWith('# ')) return <h2 key={index}>{trimmed.slice(2)}</h2>;
      if (trimmed.startsWith('## ')) return <h3 key={index}>{trimmed.slice(3)}</h3>;
      if (trimmed.startsWith('- ')) return <p key={index} className="bullet-line">{trimmed}</p>;
      return <p key={index}>{trimmed}</p>;
    });

  return (
    <div className="site-shell">
      <header className="topbar">
        <a href="#top" className="wordmark">Yusuf Jabbar</a>
        <nav aria-label="Primary navigation">
          {/* <a href="#work">Work</a>
          <a href="#career">Career</a>
          <a href="#photos">Photos</a>
          <a href="#writing">Writing</a>
          <a href="#contact">Contact</a> */}
        </nav>
      </header>

      <main id="top">
        <section className="hero">
          <div className="intro">
            <p className="eyebrow">Electrical Engineering</p>
            <p>
              Interested in FPGA prototyping, embedded and control systems, and computer vision
            </p>
            <div className="link-row">
              <a href="https://github.com/shmoos" aria-label="GitHub">
                <Github size={18} />
              </a>
              <a href="https://www.linkedin.com/in/yusufjabbar/" aria-label="LinkedIn">
                <Linkedin size={18} />
              </a>
              <a href="/YusufJabbarResume.pdf" aria-label="Resume">
                <FileText size={18} />
              </a>
              <a href="mailto:yjabbar84@gmail.com" aria-label="Email">
                <Mail size={18} />
              </a>
            </div>
          </div>
          <img src="/IMG_2769.jpg" alt="Friends gathered around a fire in foggy California hills" className="hero-image" />
        </section>

        <section id="work" className="content-section">
          <div className="section-heading">
            <span>01</span>
            <h2>Projects</h2>
          </div>
          <div className="project-list">
            {projects.map((project) => (
              <button key={project.title} type="button" className="project-row" onClick={() => setSelectedProject(project)}>
                <span className="project-year">{project.year}</span>
                <span className="project-main">
                  <strong>{project.title}</strong>
                  <small>{project.description}</small>
                </span>
                <span className="project-tags">{project.tags.join(' / ')}</span>
                <ArrowUpRight size={17} />
              </button>
            ))}
          </div>
        </section>

        <section id="career" className="content-section two-column">
          <div className="section-heading">
            <span>02</span>
            <h2>Career</h2>
          </div>
          <div className="dense-stack">
            {careerEntries.map((entry) => (
              <article key={`${entry.company}-${entry.role}`} className="resume-row">
                <div>
                  <strong>{entry.company}</strong>
                  <span>{entry.dates}</span>
                </div>
                <div>
                  <h3>{entry.role}</h3>
                  <p>{entry.location}</p>
                  <ul>
                    {entry.highlights.map((highlight) => (
                      <li key={highlight}>{highlight}</li>
                    ))}
                  </ul>
                  <p className="meta-line">{entry.technologies.join(' / ')}</p>
                </div>
              </article>
            ))}
          </div>
        </section>

        <section id="photos" className="content-section two-column">
          <div className="section-heading">
            <span>03</span>
            <h2>Photos</h2>
          </div>
          <div className="photo-strip">
            {photos.map((album) => (
              <button key={album.title} type="button" className="photo-tile" onClick={() => setSelectedAlbum(album)}>
                <img src={resolvePublicAsset(album.image)} alt={album.title} />
                <span>
                  <strong>{album.title}</strong>
                  <small>{album.count} photos</small>
                </span>
              </button>
            ))}
          </div>
        </section>

        <section id="writing" className="content-section">
          <div className="section-heading">
            <span>04</span>
            <h2>Writing</h2>
          </div>
          <div className="writing-list">
            {blogPosts.map((post) => (
              <button key={post.title} type="button" className="writing-row" onClick={() => setSelectedBlog(post)}>
                <span>{post.date}</span>
                <strong>
                  {post.title}
                  {post.protected ? <Lock size={14} /> : null}
                </strong>
                <small>{post.excerpt}</small>
                <em>{post.readTime}</em>
              </button>
            ))}
          </div>
        </section>

        <section id="contact" className="contact-section">
          <p>Yusuf Jabbar</p>
          {/* <a href="mailto:yjabbar84@gmail.com">yjabbar84@gmail.com</a> */}
        </section>
      </main>

      {selectedProject ? (
        <Modal title={selectedProject.title} onClose={() => setSelectedProject(null)}>
          <img src={resolvePublicAsset(selectedProject.image)} alt={selectedProject.title} className="modal-image" />
          <p className="modal-date">{selectedProject.year}</p>
          <p>{selectedProject.detailDescription || selectedProject.description}</p>
          <p className="meta-line">{selectedProject.tags.join(' / ')}</p>
          {selectedProject.repoUrl ? (
            <a href={selectedProject.repoUrl} target="_blank" rel="noreferrer" className="modal-link">
              View source on GitHub <ArrowUpRight size={16} />
            </a>
          ) : null}
        </Modal>
      ) : null}

      {selectedBlog ? (
        <Modal title={selectedBlog.title} onClose={() => setSelectedBlog(null)} wide>
          <div className="blog-modal-grid">
            <article className="blog-content">
              <p className="modal-date">{selectedBlog.date} / {selectedBlog.readTime}</p>
              {selectedBlog.protected && !selectedBlogContent ? (
                <form onSubmit={handleUnlockBlog} className="unlock-form">
                  <p>Enter the password to view this post.</p>
                  <input
                    id="blog-password"
                    type="password"
                    value={blogPasswordInput}
                    onChange={(event) => setBlogPasswordInput(event.target.value)}
                    placeholder="Password"
                    autoComplete="current-password"
                  />
                  {blogUnlockError ? <p className="form-error">{blogUnlockError}</p> : null}
                  <button type="submit" disabled={!blogPasswordInput.trim() || isUnlockingBlog}>
                    {isUnlockingBlog ? 'Unlocking...' : 'Unlock post'}
                  </button>
                </form>
              ) : (
                renderPostContent(selectedBlogContent)
              )}
            </article>
            {selectedBlog.sidebarImages?.length ? (
              <aside className="blog-images">
                {selectedBlog.sidebarImages.map((image, index) => (
                  <button key={`${selectedBlog.title}-${index}`} type="button" onClick={() => setSelectedBlogImage(image)}>
                    <img src={resolvePublicAsset(image)} alt={`${selectedBlog.title} ${index + 1}`} />
                  </button>
                ))}
              </aside>
            ) : null}
          </div>
        </Modal>
      ) : null}

      {selectedAlbum ? (
        <Modal title={selectedAlbum.title} onClose={() => setSelectedAlbum(null)} wide>
          <div className="album-grid">
            {currentAlbumPhotos.map((image, index) => (
              <button key={`${selectedAlbum.title}-${index}`} type="button" onClick={() => setSelectedPhoto(image)}>
                <img src={resolvePublicAsset(image)} alt={`${selectedAlbum.title} ${index + 1}`} />
              </button>
            ))}
          </div>
        </Modal>
      ) : null}

      {selectedBlogImage ? (
        <ImageOverlay image={selectedBlogImage} alt="Blog preview" onClose={() => setSelectedBlogImage(null)} />
      ) : null}

      {selectedPhoto ? (
        <ImageOverlay
          image={selectedPhoto}
          alt={`${selectedAlbum?.title || 'Photo'} preview`}
          onClose={() => setSelectedPhoto(null)}
          indexLabel={`${selectedPhotoIndex + 1} / ${currentAlbumPhotos.length}`}
          onPrevious={currentAlbumPhotos.length > 1 ? showPreviousPhoto : null}
          onNext={currentAlbumPhotos.length > 1 ? showNextPhoto : null}
        />
      ) : null}
    </div>
  );
}

function Modal({ title, children, onClose, wide = false }) {
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <section className={`modal-panel ${wide ? 'wide' : ''}`} onClick={(event) => event.stopPropagation()}>
        <header className="modal-header">
          <h2>{title}</h2>
          <button type="button" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </header>
        {children}
      </section>
    </div>
  );
}

function ImageOverlay({ image, alt, onClose, indexLabel, onPrevious, onNext }) {
  return (
    <div className="image-overlay" onClick={onClose}>
      <div className="image-stage" onClick={(event) => event.stopPropagation()}>
        <div className="image-bar">
          <button type="button" onClick={onClose}>Close</button>
          {indexLabel ? <span>{indexLabel}</span> : null}
        </div>
        <div className="image-frame">
          {onPrevious ? (
            <button type="button" className="image-nav previous" onClick={onPrevious} aria-label="Previous photo">
              <ChevronLeft size={22} />
            </button>
          ) : null}
          <img src={resolvePublicAsset(image)} alt={alt} />
          {onNext ? (
            <button type="button" className="image-nav next" onClick={onNext} aria-label="Next photo">
              <ChevronRight size={22} />
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
